import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider
} from "@mui/material";
import {
  X,
  Calendar,
  UploadCloud,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  Trash2,
  Send,
  FileImage,
  History,
  AlertTriangle
} from "lucide-react";
import RejectReasonDialog from "./RejectReasonDialog";
import {
  getDesignerPosterPresignedUploadUrl,
  uploadFileToS3,
  createDesignerSocialMediaUpload,
  getFilePresignedViewUrl,
  deleteS3Object,
  fetchDesignerEventUploadPosters,
  deleteDesignerSocialMediaUpload,
  fetchAdminSocialMediaUploadFiles,
  reviewAdminSocialMediaUploadRequest
} from "../../../../services/eventUploadsService";

const SocialPosterModal = ({
  isOpen,
  onClose,
  event,
  onUpdateEvent,
  onPreviewFile,
  userRole = "designer"
}) => {
  const [rejectDialog, setRejectDialog] = useState({ isOpen: false });

  // Separate state for existing uploaded files (from DB) vs new staged files (uploaded in this session)
  const [existingPosters, setExistingPosters] = useState([]);
  const [stagedPosters, setStagedPosters] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    isOpen: false,
    poster: null
  });
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);

  const bookslotId = event?.bookslot_id;
  const requestId =
    event?.request_id ||
    event?.poster_request_id ||
    event?.event_upload_request_id ||
    event?.social_media_request_id ||
    event?.id;
  const instituteName = event?.institute_name || "Unknown Institute";
  const eventType = (event?.event_type || "FDP").toUpperCase();
  const startDate = event?.start_date || "";
  const endDate = event?.end_date || "";
  const dateRange = startDate && endDate ? `${startDate} – ${endDate}` : "—";
  const domainName = event?.domain_name || "";
  const feedbackText = event?.reason || event?.socialPosterFeedback || "";

  // Exact backend status: "pending" | "approved" | "rejected" | null
  const status =
    event?.poster_upload_status ||
    event?.status ||
    event?.socialPosterStatus;

  const isApproved = status === "approved";
  const isPending = status === "pending";
  const isRejected = status === "rejected";

  // Designer can upload ONLY when status is null (no upload yet) or rejected
  const canUpload = userRole !== "admin" && (!status || status === "rejected");

  // Initialize or fetch existing files from backend on modal open
  useEffect(() => {
    if (!isOpen || !event) return;

    setErrorMessage("");
    setStagedPosters([]); // reset newly staged files on open

    const rawExisting =
      event.socialPosters ||
      event.social_media_posters ||
      event.social_media_files ||
      event.files ||
      [];

    if (rawExisting.length > 0) {
      Promise.all(
        rawExisting.map(async (p, idx) => {
          let viewUrl = p.url || p.preview || null;
          if (!viewUrl && p.key_name) {
            try {
              viewUrl = await getFilePresignedViewUrl(p.key_name);
            } catch (err) {
              console.warn("Could not fetch view url for poster:", err);
            }
          }

          return {
            id: p.id || p.upload_id || `existing-sp-${idx}`,
            name: p.name || p.file_name || "Social Media Poster",
            size: p.size || (p.file_size ? `${(p.file_size / (1024 * 1024)).toFixed(1)} MB` : "Image"),
            type: p.type || "image/png",
            url: viewUrl,
            key_name: p.key_name || null,
            presigned_url: null,
            isUploading: false,
            isSuccess: true,
            isExisting: true,
            uploadedAt: p.uploadedAt || (p.uploaded_at ? new Date(p.uploaded_at).toLocaleDateString() : "Uploaded"),
            status: p.status || status || "approved"
          };
        })
      ).then((loadedPosters) => {
        setExistingPosters(loadedPosters);
      });
    } else if (requestId) {
      setIsLoadingFiles(true);
      const fetchFilesPromise =
        userRole === "admin"
          ? fetchAdminSocialMediaUploadFiles(requestId)
          : fetchDesignerEventUploadPosters(requestId);

      fetchFilesPromise
        .then(async (res) => {
          const files = res?.files || [];
          const filesWithUrls = await Promise.all(
            files.map(async (f) => {
              let viewUrl = null;
              if (f.key_name) {
                try {
                  viewUrl = await getFilePresignedViewUrl(f.key_name);
                } catch {
                  viewUrl = null;
                }
              }
              return {
                id: f.id,
                name: f.file_name,
                url: viewUrl,
                key_name: f.key_name,
                uploadedAt: f.uploaded_at
                  ? new Date(f.uploaded_at).toLocaleDateString()
                  : "Uploaded",
                size: "Image",
                status: res.status || status || "pending",
                isUploading: false,
                isSuccess: true,
                isExisting: true
              };
            })
          );
          setExistingPosters(filesWithUrls);
        })
        .catch((err) => {
          console.error("Failed to fetch existing social media files:", err);
          setExistingPosters([]);
        })
        .finally(() => {
          setIsLoadingFiles(false);
        });
    } else {
      setExistingPosters([]);
    }
  }, [isOpen, event, requestId, userRole, status]);

  // Direct S3 Presigned Upload Handler for NEW files
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setErrorMessage("");

    // Create temporary entries in uploading state in stagedPosters
    const newItems = files.map((file, idx) => {
      const tempId = `staged-sp-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 6)}`;
      const isImg = file.type.startsWith("image/");
      return {
        id: tempId,
        name: file.name,
        size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        type: file.type || "image/png",
        url: isImg ? URL.createObjectURL(file) : null,
        fileObj: file,
        key_name: null,
        presigned_url: null,
        isUploading: true,
        isSuccess: false,
        isNew: true,
        uploadProgress: 0,
        error: null,
        uploadedAt: "Just now",
        status: "pending"
      };
    });

    setStagedPosters((prev) => [...prev, ...newItems]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Upload each file directly to S3 via presigned PUT URL
    for (const entry of newItems) {
      try {
        // Step 1: Request presigned S3 PUT URL from backend
        const presignedData = await getDesignerPosterPresignedUploadUrl(entry.name);
        const { presigned_url, key_name } = presignedData || {};

        if (!presigned_url || !key_name) {
          throw new Error("No presigned upload URL returned from server.");
        }

        // Step 2: Upload file binary directly to S3 with progress updates
        await uploadFileToS3(presigned_url, entry.fileObj, (progress) => {
          setStagedPosters((prev) =>
            prev.map((item) =>
              item.id === entry.id ? { ...item, uploadProgress: progress } : item
            )
          );
        });

        // Step 3: Fetch authenticated GET view URL for immediate high-res preview
        let viewUrl = entry.url;
        if (key_name) {
          try {
            const fetchedUrl = await getFilePresignedViewUrl(key_name);
            if (fetchedUrl) viewUrl = fetchedUrl;
          } catch (viewErr) {
            console.warn("Could not retrieve presigned view URL immediately:", viewErr);
          }
        }

        // Step 4: Mark file ready with S3 key_name
        setStagedPosters((prev) =>
          prev.map((item) =>
            item.id === entry.id
              ? {
                  ...item,
                  key_name: key_name,
                  presigned_url: presigned_url,
                  url: viewUrl,
                  isUploading: false,
                  isSuccess: true,
                  uploadProgress: 100
                }
              : item
          )
        );
      } catch (uploadErr) {
        console.error(`Error uploading ${entry.name}:`, uploadErr);
        const errDetail =
          uploadErr?.message || `Failed to upload ${entry.name}.`;
        setErrorMessage(errDetail);

        setStagedPosters((prev) =>
          prev.map((item) =>
            item.id === entry.id
              ? {
                  ...item,
                  isUploading: false,
                  isSuccess: false,
                  error: errDetail
                }
              : item
          )
        );
      }
    }
  };

  // Remove Staged New File Handler (before submit)
  const handleRemoveStagedFile = (fileId) => {
    const target = stagedPosters.find((f) => f.id === fileId);
    if (target?.key_name) {
      deleteS3Object(target.key_name).catch(() => {});
    }
    setStagedPosters((prev) => prev.filter((f) => f.id !== fileId));
  };

  // Delete Existing Uploaded Poster via DELETE /event_uploads/designer/delete-social-media-uploads/{upload_id}
  const handleConfirmDeletePoster = async () => {
    const poster = deleteConfirmDialog.poster;
    if (!poster?.id) return;
    try {
      setDeletingId(poster.id);
      setErrorMessage("");
      await deleteDesignerSocialMediaUpload(poster.id);
      setExistingPosters((prev) => prev.filter((p) => p.id !== poster.id));
      setDeleteConfirmDialog({ isOpen: false, poster: null });
    } catch (err) {
      console.error("Failed to delete social media upload:", err);
      setErrorMessage(
        err?.message || "Failed to delete uploaded poster file."
      );
    } finally {
      setDeletingId(null);
    }
  };

  // Preview File Handler
  const handlePreviewFile = async (poster) => {
    try {
      let targetUrl = poster.url;
      if (!targetUrl && poster.key_name) {
        targetUrl = await getFilePresignedViewUrl(poster.key_name);
      }
      if (onPreviewFile) {
        onPreviewFile({ ...poster, url: targetUrl || poster.url });
      }
    } catch (err) {
      console.warn("Failed to preview poster:", err);
      if (onPreviewFile) {
        onPreviewFile(poster);
      }
    }
  };

  // Designer Submit to Admin — Calls POST /designer/social-media-uploads ONLY with NEW files!
  const handleDesignerSubmit = async () => {
    setErrorMessage("");

    const isAnyUploading = stagedPosters.some((f) => f.isUploading);
    if (isAnyUploading) {
      setErrorMessage("Please wait for all new posters to finish uploading.");
      return;
    }

    const newValidPosters = stagedPosters.filter((f) => f.key_name && f.isSuccess !== false);
    if (newValidPosters.length === 0) {
      setErrorMessage("Please upload at least one new social media poster before submitting.");
      return;
    }

    if (!bookslotId) {
      setErrorMessage("Missing bookslot ID for this event.");
      return;
    }

    try {
      setIsSubmitting(true);

      // ONLY send newly uploaded files in the payload to avoid inserting duplicates!
      const payload = {
        bookslot_id: Number(bookslotId),
        files: newValidPosters.map((p) => ({
          file_name: p.name,
          key_name: p.key_name
        }))
      };

      const res = await createDesignerSocialMediaUpload(payload);

      const updatedEvent = {
        ...event,
        socialPosters: newValidPosters,
        status: "pending",
        socialPosterStatus: "pending",
        social_media_request_id: res?.social_media_request_id || event?.social_media_request_id,
        socialPosterSubmittedAt: new Date().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        })
      };

      if (onUpdateEvent) {
        onUpdateEvent(updatedEvent);
      }
      onClose();
    } catch (err) {
      console.error("Error submitting social media posters to backend:", err);
      setErrorMessage(
        err?.message || "Failed to submit social media posters. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Admin Approve Social Poster — Calls PATCH /admin/social-media-upload-requests/{request_id}/review
  const handleAdminApprove = async () => {
    setErrorMessage("");
    if (requestId) {
      try {
        setIsSubmitting(true);
        await reviewAdminSocialMediaUploadRequest(requestId, { action: "approved" });
      } catch (err) {
        console.error("Error approving social poster:", err);
        setErrorMessage(err?.message || "Failed to approve social poster.");
        setIsSubmitting(false);
        return;
      } finally {
        setIsSubmitting(false);
      }
    }

    const updatedEvent = {
      ...event,
      socialPosterStatus: "approved",
      status: "approved",
      socialPosterFeedback: "",
      socialPosters: existingPosters.map((p) => ({ ...p, status: "approved" }))
    };
    if (onUpdateEvent) {
      onUpdateEvent(updatedEvent);
    }
    onClose();
  };

  // Admin Reject Social Poster — Calls PATCH /admin/social-media-upload-requests/{request_id}/review
  const handleConfirmReject = async (reason) => {
    setErrorMessage("");
    if (requestId) {
      try {
        setIsSubmitting(true);
        await reviewAdminSocialMediaUploadRequest(requestId, {
          action: "rejected",
          reason: reason || "Rejected by admin"
        });
      } catch (err) {
        console.error("Error rejecting social poster:", err);
        setErrorMessage(err?.message || "Failed to submit rejection.");
        setIsSubmitting(false);
        return;
      } finally {
        setIsSubmitting(false);
      }
    }

    const updatedEvent = {
      ...event,
      socialPosterStatus: "rejected",
      status: "rejected",
      socialPosterFeedback: reason,
      reason: reason,
      socialPosters: existingPosters.map((p) => ({ ...p, status: "rejected" }))
    };
    if (onUpdateEvent) {
      onUpdateEvent(updatedEvent);
    }
    setRejectDialog({ isOpen: false });
    onClose();
  };

  // Helper for Status Badge — Shows exact backend status
  const renderStatusChip = () => {
    const statusLabel = status
      ? status.charAt(0).toUpperCase() + status.slice(1)
      : "Not Uploaded";

    if (isApproved) {
      return (
        <Chip
          icon={<CheckCircle2 size={13} color="#16a34a" />}
          label={statusLabel}
          size="small"
          sx={{
            height: "24px",
            fontSize: "0.72rem",
            fontWeight: 700,
            backgroundColor: "#f0fdf4",
            color: "#16a34a",
            border: "1px solid #bbf7d0",
            borderRadius: "6px"
          }}
        />
      );
    }
    if (isRejected) {
      return (
        <Chip
          icon={<XCircle size={13} color="#dc2626" />}
          label={statusLabel}
          size="small"
          sx={{
            height: "24px",
            fontSize: "0.72rem",
            fontWeight: 700,
            backgroundColor: "#fef2f2",
            color: "#dc2626",
            border: "1px solid #fecaca",
            borderRadius: "6px"
          }}
        />
      );
    }
    if (isPending) {
      return (
        <Chip
          icon={<Clock size={13} color="#d97706" />}
          label={statusLabel}
          size="small"
          sx={{
            height: "24px",
            fontSize: "0.72rem",
            fontWeight: 700,
            backgroundColor: "#fffbeb",
            color: "#d97706",
            border: "1px solid #fde68a",
            borderRadius: "6px"
          }}
        />
      );
    }
    return (
      <Chip
        label={statusLabel}
        size="small"
        sx={{
          height: "24px",
          fontSize: "0.72rem",
          fontWeight: 600,
          backgroundColor: "#f1f5f9",
          color: "#64748b",
          border: "1px solid #cbd5e1",
          borderRadius: "6px"
        }}
      />
    );
  };


  // Total display count (for Admin: existingPosters; for Designer: staged + existing)
  const totalCount =
    userRole === "admin"
      ? existingPosters.length
      : stagedPosters.length + existingPosters.length;

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            overflow: "hidden",
            maxHeight: "92vh"
          }
        }}
      >
        {/* Modal Header */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "1px solid #e2e8f0",
            py: 2,
            px: 3,
            backgroundColor: "#f8fafc"
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5, flexWrap: "wrap" }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.05rem" }}
              >
                {instituteName}
              </Typography>
              <Chip
                label={eventType}
                size="small"
                sx={{
                  height: "20px",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  backgroundColor: eventType === "FDP" ? "#eff6ff" : "#f0fdf4",
                  color: eventType === "FDP" ? "#1877f2" : "#16a34a",
                  border: `1px solid ${
                    eventType === "FDP" ? "#bfdbfe" : "#bbf7d0"
                  }`,
                  borderRadius: "4px"
                }}
              />
              {renderStatusChip()}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                color: "#64748b",
                fontSize: "0.78rem"
              }}
            >
              <Calendar size={13} color="#1877f2" />
              <span>{dateRange}</span>
              {(event?.domainName || event?.domain_name) && (
                <>
                  <span>•</span>
                  <span style={{ fontWeight: 600, color: "#475569" }}>
                    {event.domainName || event.domain_name}
                  </span>
                </>
              )}
            </Box>
          </Box>

          <IconButton onClick={onClose} size="small" sx={{ color: "#94a3b8" }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>

        {/* Modal Body */}
        <DialogContent
          dividers
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            backgroundColor: "#ffffff"
          }}
        >
          {/* Error Message */}
          {errorMessage && (
            <Alert
              severity="error"
              onClose={() => setErrorMessage("")}
              sx={{
                borderRadius: "8px",
                fontSize: "0.8125rem"
              }}
            >
              {errorMessage}
            </Alert>
          )}

          {/* Admin Rejection Alert Banner */}
          {isRejected && feedbackText && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "8px",
                fontSize: "0.8125rem",
                "& .MuiAlert-message": { width: "100%" }
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
                Admin Rejection Feedback:
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.8rem", mt: 0.25 }}>
                {feedbackText}
              </Typography>
            </Alert>
          )}

          {/* Pending Status Info Banner for Designer */}
          {isPending && userRole === "designer" && (
            <Alert
              severity="info"
              sx={{
                borderRadius: "8px",
                fontSize: "0.8125rem"
              }}
            >
              Your social media poster submission has been submitted and is currently pending review by the admin.
            </Alert>
          )}

          {/* Social Poster Upload Box (Designer only when status is null or rejected) */}
          {canUpload && (
            <Box>
              <Box
                component="label"
                sx={{
                  border: "2px dashed #93c5fd",
                  borderRadius: "12px",
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  pointerEvents: isSubmitting ? "none" : "auto",
                  backgroundColor: "#f0f7ff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#3b82f6",
                    backgroundColor: "#e0f2fe"
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  disabled={isSubmitting}
                  multiple
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  onChange={handleFileUpload}
                />
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    backgroundColor: "#dbeafe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                    color: "#1877f2"
                  }}
                >
                  <UploadCloud size={22} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#0f172a",
                    fontSize: "0.9rem",
                    mb: 0.5
                  }}
                >
                  Click or drag & drop to upload new Social Poster
                </Typography>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "0.75rem",
                    textAlign: "center"
                  }}
                >
                  Supported formats: High-res PNG, JPG, JPEG, WEBP (Square / Landscape format recommended)
                </Typography>
              </Box>
            </Box>
          )}

          {/* Loading Indicator */}
          {isLoadingFiles && (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress size={28} sx={{ color: "#1877f2", mb: 1 }} />
              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                Loading uploaded posters...
              </Typography>
            </Box>
          )}

          {/* ── SECTION 1: NEW STAGED POSTERS (Uploaded in current session) ── */}
          {stagedPosters.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.85rem", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
              >
                <span>New Replacement Posters ({stagedPosters.length})</span>
                <Chip label="Ready to submit" size="small" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, backgroundColor: "#eff6ff", color: "#1877f2" }} />
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(260px, 1fr))" },
                  gap: 2
                }}
              >
                {stagedPosters.map((poster) => (
                  <Paper
                    key={poster.id}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: "10px",
                      border: poster.error
                        ? "1px solid #fca5a5"
                        : poster.isSuccess
                        ? "1px solid #bbf7d0"
                        : "1px solid #e2e8f0",
                      backgroundColor: "#ffffff",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "all 0.15s ease",
                      "&:hover": { borderColor: "#93c5fd", boxShadow: "0 4px 14px rgba(0,0,0,0.06)" }
                    }}
                  >
                    {/* Thumbnail / Uploading Box */}
                    <Box
                      onClick={() => !poster.isUploading && handlePreviewFile(poster)}
                      sx={{
                        width: "100%",
                        height: 150,
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#f1f5f9",
                        cursor: poster.isUploading ? "default" : "pointer",
                        border: "1px solid #e2e8f0",
                        mb: 1.25,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {poster.isUploading ? (
                        <Box sx={{ width: "80%", textAlign: "center" }}>
                          <CircularProgress size={28} sx={{ color: "#1877f2", mb: 1 }} />
                          <LinearProgress
                            variant="determinate"
                            value={poster.uploadProgress}
                            sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                          />
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "#64748b" }}>
                            Uploading... {poster.uploadProgress}%
                          </Typography>
                        </Box>
                      ) : poster.url ? (
                        <img
                          src={poster.url}
                          alt={poster.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#94a3b8"
                          }}
                        >
                          <FileImage size={32} />
                        </Box>
                      )}
                    </Box>

                    {/* Metadata */}
                    <Box sx={{ mb: 1.25 }}>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                        title={poster.name}
                      >
                        {poster.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", display: "block" }}>
                        {poster.size} • {poster.uploadedAt}
                      </Typography>
                      {poster.error && (
                        <Typography variant="caption" sx={{ color: "#dc2626", fontSize: "0.6875rem", display: "block", mt: 0.25 }}>
                          {poster.error}
                        </Typography>
                      )}
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: "flex", gap: 1, pt: 1, borderTop: "1px solid #f1f5f9" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Eye size={13} />}
                        onClick={() => handlePreviewFile(poster)}
                        disabled={poster.isUploading}
                        sx={{
                          flex: 1,
                          py: 0.4,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          borderRadius: "6px",
                          textTransform: "none",
                          color: "#475569",
                          borderColor: "#cbd5e1",
                          "&:hover": { backgroundColor: "#f8fafc" }
                        }}
                      >
                        Preview
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Trash2 size={13} />}
                        onClick={() => handleRemoveStagedFile(poster.id)}
                        disabled={poster.isUploading}
                        sx={{
                          py: 0.4,
                          px: 1.2,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          borderRadius: "6px",
                          textTransform: "none",
                          borderColor: "#fecaca",
                          color: "#dc2626",
                          "&:hover": { backgroundColor: "#fef2f2" }
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* ── SECTION 2: PREVIOUSLY UPLOADED / EXISTING POSTERS ── */}
          {existingPosters.length > 0 && (
            <Box>
              {stagedPosters.length > 0 && <Divider sx={{ my: 1 }} />}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "#475569",
                  fontSize: "0.85rem",
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75
                }}
              >
                <History size={15} color="#64748b" />
                <span>
                  {userRole === "admin"
                    ? `Uploaded Posters (${existingPosters.length})`
                    : `Previously Uploaded Posters (${existingPosters.length})`}
                </span>
                {isRejected && userRole === "designer" && (
                  <Chip
                    label="Previous (Rejected)"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      backgroundColor: "#fef2f2",
                      color: "#dc2626",
                      border: "1px solid #fecaca"
                    }}
                  />
                )}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(260px, 1fr))" },
                  gap: 2
                }}
              >
                {existingPosters.map((poster) => (
                  <Paper
                    key={poster.id}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      backgroundColor: isRejected && userRole === "designer" ? "#fafafa" : "#ffffff",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "all 0.15s ease",
                      "&:hover": { borderColor: "#93c5fd", boxShadow: "0 4px 14px rgba(0,0,0,0.06)" }
                    }}
                  >
                    {/* Thumbnail Box */}
                    <Box
                      onClick={() => handlePreviewFile(poster)}
                      sx={{
                        width: "100%",
                        height: 150,
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#f1f5f9",
                        cursor: "pointer",
                        border: "1px solid #e2e8f0",
                        mb: 1.25,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {poster.url ? (
                        <img
                          src={poster.url}
                          alt={poster.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#94a3b8"
                          }}
                        >
                          <FileImage size={32} />
                        </Box>
                      )}
                    </Box>

                    {/* Metadata */}
                    <Box sx={{ mb: 1.25 }}>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                        title={poster.name}
                      >
                        {poster.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", display: "block" }}>
                        {poster.size} • Uploaded {poster.uploadedAt}
                      </Typography>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: "flex", gap: 1, pt: 1, borderTop: "1px solid #f1f5f9" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Eye size={13} />}
                        onClick={() => handlePreviewFile(poster)}
                        sx={{
                          flex: 1,
                          py: 0.4,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          borderRadius: "6px",
                          textTransform: "none",
                          color: "#475569",
                          borderColor: "#cbd5e1",
                          "&:hover": { backgroundColor: "#f8fafc" }
                        }}
                      >
                        Preview
                      </Button>

                      {canUpload && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Trash2 size={13} />}
                          onClick={() => setDeleteConfirmDialog({ isOpen: true, poster })}
                          disabled={Boolean(deletingId)}
                          sx={{
                            py: 0.4,
                            px: 1.2,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            borderRadius: "6px",
                            textTransform: "none",
                            borderColor: "#fecaca",
                            color: "#dc2626",
                            "&:hover": { backgroundColor: "#fef2f2" }
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* Empty state when no files exist */}
          {!isLoadingFiles && totalCount === 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: "10px",
                backgroundColor: "#f8fafc",
                border: "1px dashed #cbd5e1"
              }}
            >
              <FileImage size={28} color="#94a3b8" style={{ marginBottom: 8 }} />
              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 500 }}>
                No social poster uploaded yet.
              </Typography>
            </Paper>
          )}
        </DialogContent>

        {/* Modal Footer Actions */}
        <DialogActions
          sx={{
            p: 2,
            px: 3,
            borderTop: "1px solid #e2e8f0",
            justifyContent: "space-between",
            backgroundColor: "#f8fafc"
          }}
        >
          <Button
            onClick={onClose}
            size="small"
            disabled={isSubmitting}
            sx={{ textTransform: "none", fontSize: "0.8rem", color: "#64748b" }}
          >
            Close
          </Button>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            {userRole === "admin" ? (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<XCircle size={15} />}
                  onClick={() => setRejectDialog({ isOpen: true })}
                  disabled={existingPosters.length === 0 || isSubmitting}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8125rem",
                    borderRadius: "6px",
                    borderColor: "#fca5a5",
                    backgroundColor: "#fef2f2",
                    "&:hover": { backgroundColor: "#fee2e2" }
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<CheckCircle2 size={15} />}
                  onClick={handleAdminApprove}
                  disabled={existingPosters.length === 0 || isSubmitting}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.8125rem",
                    borderRadius: "6px",
                    px: 2.5,
                    backgroundColor: "#16a34a",
                    "&:hover": { backgroundColor: "#15803d" }
                  }}
                >
                  Approve
                </Button>
              </>
            ) : (
              canUpload && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={15} sx={{ color: "#ffffff" }} />
                    ) : (
                      <Send size={15} />
                    )
                  }
                  onClick={handleDesignerSubmit}
                  disabled={
                    stagedPosters.length === 0 ||
                    stagedPosters.some((p) => p.isUploading) ||
                    isSubmitting
                  }
                  sx={{
                    px: 2.5,
                    py: 0.6,
                    borderRadius: "6px",
                    fontWeight: 700,
                    fontSize: "0.8125rem",
                    textTransform: "none",
                    backgroundColor: "#1877f2",
                    "&:hover": { backgroundColor: "#1465cc" }
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              )
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Reject Feedback Reason Dialog */}
      <RejectReasonDialog
        isOpen={rejectDialog.isOpen}
        onClose={() => setRejectDialog({ isOpen: false })}
        targetItemTitle={`${instituteName} Social Poster`}
        onConfirmReject={handleConfirmReject}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.isOpen}
        onClose={() => !deletingId && setDeleteConfirmDialog({ isOpen: false, poster: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              backgroundColor: "#fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#dc2626",
              flexShrink: 0
            }}
          >
            <AlertTriangle size={20} />
          </Box>
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 700, color: "#0f172a" }}>
            Delete Poster File?
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.6 }}>
            Are you sure you want to permanently delete{" "}
            <strong style={{ color: "#0f172a" }}>
              {deleteConfirmDialog.poster?.name || "this file"}
            </strong>
            ? This action cannot be undone and will delete the record from the database.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteConfirmDialog({ isOpen: false, poster: null })}
            size="small"
            disabled={Boolean(deletingId)}
            sx={{
              textTransform: "none",
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "#64748b"
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirmDeletePoster}
            variant="contained"
            color="error"
            size="small"
            disabled={Boolean(deletingId)}
            startIcon={
              deletingId ? (
                <CircularProgress size={14} sx={{ color: "#ffffff" }} />
              ) : (
                <Trash2 size={14} />
              )
            }
            sx={{
              textTransform: "none",
              fontSize: "0.8rem",
              fontWeight: 700,
              borderRadius: "6px",
              px: 2,
              backgroundColor: "#dc2626",
              "&:hover": { backgroundColor: "#b91c1c" }
            }}
          >
            {deletingId ? "Deleting..." : "Delete Permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SocialPosterModal;
